import React, {useCallback, useRef, useState} from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {Icon, type IconName} from '@/shared/ui/icons';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {radii, shadows, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Выпадающий список, привязанный к кнопке, — та самая штука, которой в этом ките не
 * хватало.
 *
 * До него выбор одного значения из списка изображался либо ФИШКАМИ (все варианты
 * нарисованы сразу, каждый шириной со своё слово), либо выдвижным листом снизу. Первое
 * не масштабируется — четыре фильтра по три-пять вариантов превращают панель в форму на
 * три строки; второе на мониторе выглядит как окно, приехавшее не оттуда.
 *
 * Здесь одна кнопка и меню под ней: ровно то, что делает любая панель, которую человек
 * открывает каждый день. Сжатие честное — что фильтруется, по-прежнему написано НА
 * кнопке (см. `FilterPill`), так что вопрос «чем сужен список» ответ имеет и закрытым
 * меню.
 *
 * ## Две формы одного контрола
 *
 * На мониторе — всплывающая карточка у кнопки. На телефоне — тот же самый лист снизу,
 * что и у всех остальных вопросов приложения: попасть пальцем в пункт 32pt высотой,
 * висящий у края экрана, нельзя, и ни одна мобильная система этого и не предлагает.
 *
 * ## Почему координаты меряются, а не считаются
 *
 * `measureInWindow` — единственный способ узнать, где кнопка ОКАЗАЛАСЬ: она может
 * стоять внутри прокрутки, внутри переносящейся строки, на любой ширине окна. Меню
 * рисуется в `Modal`, то есть в своём слое поверх всего, поэтому свои координаты ему
 * взять больше неоткуда.
 *
 * Карточка прижимается к краям окна и переворачивается вверх, когда под кнопкой не
 * осталось места, — иначе меню у нижней строки таблицы уезжает за экран.
 */
export type Anchor = {x: number; y: number; width: number; height: number};

/** Сколько места под кнопкой считается «слишком мало» — тогда меню открывается вверх. */
const MIN_BELOW = 180;
const GAP = 6;
const EDGE = 8;

const styles = StyleSheet.create({
  backdrop: {flex: 1},
  /* Всплывающая карточка: `surface-overlay`, рамка `border-strong`, плотная тень —
     так всплывающее отличается от врезанного. Радиус 3. */
  card: {
    position: 'absolute',
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    paddingVertical: spacing(1),
    overflow: 'hidden',
  },
  body: {paddingHorizontal: spacing(1)},

  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    minHeight: 30,
    paddingHorizontal: spacing(2),
    paddingVertical: spacing(1),
    borderRadius: radii.xs,
  },
  optionPhone: {minHeight: 44, paddingHorizontal: spacing(3)},
  optionLabel: {flex: 1, minWidth: 0, gap: 1},
  leading: {width: 20, alignItems: 'center', justifyContent: 'center'},
  check: {width: 16, alignItems: 'center'},
  label: {paddingHorizontal: spacing(2), paddingTop: spacing(2), paddingBottom: spacing(1)},
  separator: {height: StyleSheet.hairlineWidth, marginVertical: spacing(1), marginHorizontal: -spacing(1)},
});

/** True, пока указатель над строкой или на ней держат палец. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * Строка меню. Отмеченная несёт галочку, а не заливку: заливкой в этом ките помечают
 * контрол В ТЕКУЩЕМ ПОЛОЖЕНИИ, а строка списка — это не контрол, это вариант.
 */
export function MenuOption({
  label,
  hint,
  selected,
  onPress,
  icon,
  leading,
  danger = false,
  testID,
}: {
  label: string;
  /** Вторая строка — роль, вид контекста. */
  hint?: string;
  selected: boolean;
  onPress: () => void;
  /** Глиф слева — у пунктов-глаголов (аккаунт, выход). */
  icon?: IconName;
  /** Произвольная метка слева — точка статуса, аватар. */
  leading?: React.ReactNode;
  /** Красная краска — «выйти», «удалить». */
  danger?: boolean;
  testID?: string;
}) {
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const ink = danger ? p.destructiveInk : p.foreground;

  return (
    <Pressable
      testID={testID}
      accessibilityRole="menuitem"
      accessibilityState={{selected}}
      onPress={onPress}
      style={state => [
        styles.option,
        !isDesktop && styles.optionPhone,
        isHot(state) && {backgroundColor: danger ? p.destructiveSoft : p.accent},
      ]}>
      {leading ? (
        <View style={styles.leading}>{leading}</View>
      ) : icon ? (
        <View style={styles.leading}>
          <Icon name={icon} size={16} color={danger ? p.destructiveInk : p.mutedForeground} />
        </View>
      ) : null}
      <View style={styles.optionLabel}>
        <AppText variant="body" color={selected ? p.primaryInk : ink} numberOfLines={1}>
          {label}
        </AppText>
        {hint ? (
          <AppText variant="caption" numberOfLines={1}>
            {hint}
          </AppText>
        ) : null}
      </View>
      {/* Галочка стоит справа — как в shadcn `DropdownMenuCheckboxItem`; слева
          оставалось бы пустое место у каждого неотмеченного пункта. */}
      {selected ? (
        <View style={styles.check}>
          <Icon name="check" size={14} color={p.primaryInk} />
        </View>
      ) : null}
    </Pressable>
  );
}

/** Подпись группы пунктов — shadcn `DropdownMenuLabel`. */
export function MenuLabel({children}: {children: string}) {
  return (
    <View style={styles.label}>
      <AppText variant="eyebrow" numberOfLines={1}>
        {children}
      </AppText>
    </View>
  );
}

/** Черта между группами — shadcn `DropdownMenuSeparator`. */
export function MenuSeparator() {
  const p = useTheme();
  return <View style={[styles.separator, {backgroundColor: p.border}]} />;
}

/**
 * Слой меню. На мониторе — карточка у кнопки, на телефоне — лист снизу.
 *
 * `title` виден только в листе: у карточки под кнопкой заголовок избыточен — кнопка,
 * из которой она выехала, стоит в двух пикселях выше и уже назвала себя.
 */
export function Menu({
  visible,
  onClose,
  anchor,
  title,
  minWidth = 220,
  children,
  testID,
}: {
  visible: boolean;
  onClose: () => void;
  /** Где стоит кнопка. `null` — ещё не измерена, меню не рисуется. */
  anchor: Anchor | null;
  title: string;
  minWidth?: number;
  children: React.ReactNode;
  testID?: string;
}) {
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const {width: winWidth, height: winHeight} = useWindowDimensions();

  if (!isDesktop) {
    return (
      <Sheet visible={visible} onClose={onClose} title={title} testID={testID}>
        {children}
      </Sheet>
    );
  }

  if (!anchor) {
    return null;
  }

  const below = winHeight - (anchor.y + anchor.height) - GAP - EDGE;
  const flip = below < MIN_BELOW && anchor.y > below;
  const maxHeight = Math.max(
    120,
    flip ? anchor.y - GAP - EDGE : below,
  );
  const width = Math.max(minWidth, anchor.width);
  // Прижать к правому краю окна, но не левее его левого края.
  const left = Math.max(EDGE, Math.min(anchor.x, winWidth - width - EDGE));

  const position: StyleProp<ViewStyle> = flip
    ? {left, width, bottom: winHeight - anchor.y + GAP}
    : {left, width, top: anchor.y + anchor.height + GAP};

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      {/* Подложка прозрачна, но кликабельна: меню закрывается нажатием мимо, как
          закрывается любое меню. */}
      <Pressable
        testID={testID ? `${testID}-backdrop` : 'menu-backdrop'}
        style={styles.backdrop}
        onPress={onClose}>
        <Pressable
          testID={testID}
          accessibilityRole="menu"
          onPress={() => {}}
          style={[
            styles.card,
            position,
            shadows.pop,
            {backgroundColor: p.popover, borderColor: p.borderStrong, maxHeight},
          ]}>
          <ScrollView
            bounces={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={styles.body}>
            {children}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/**
 * Кнопка, которая умеет сказать меню, где она стоит.
 *
 * Измерение живёт здесь, а не у вызывающего, потому что ошибиться в нём можно ровно
 * одним способом: померить ДО того, как строка перенеслась на новую ширину. Замер
 * делается на каждое открытие.
 */
export function useAnchor(): {
  ref: React.RefObject<View | null>;
  anchor: Anchor | null;
  measure: (then: () => void) => void;
} {
  const ref = useRef<View>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const measure = useCallback((then: () => void) => {
    const node = ref.current;
    if (!node) {
      then();
      return;
    }
    node.measureInWindow((x, y, width, height) => {
      setAnchor({x, y, width, height});
      then();
    });
  }, []);

  return {ref, anchor, measure};
}
